import React, { useEffect, useRef, useState } from "react";
import {
  X,
  CaretRight,
  Camera,
  Sparkle,
  Keyboard,
  CircleNotch,
} from "@phosphor-icons/react";
import { toast } from "sonner";
import api from "../../../lib/api";
import { usePaywall } from "../../../lib/paywall";
import { resizeToDataUrl } from "../../../lib/imageUtils";
import { TextField } from "./FormPrimitives";

/* ---------- Method picker — same visual as PhotoIdMethodPicker ---------- */
export function FlightMethodPicker({ onPick, onCancel }) {
  const methods = [
    {
      id: "scan",
      Icon: Sparkle,
      title: "Scan boarding pass with camera",
      sub: "Use AI to auto-fill the fields",
      color: "#E55B2C",
      soft: "#FDE6D8",
    },
    {
      id: "manual",
      Icon: Keyboard,
      title: "Enter manually",
      sub: "Type each field yourself",
      color: "#0E2C54",
      soft: "#EAF2FB",
    },
  ];
  return (
    <div
      className="mt-5 bg-white rounded-2xl shadow-soft border border-navy/10 p-4 space-y-3"
      data-testid="flight-method-picker"
    >
      <div className="flex items-center justify-between mb-1">
        <div className="font-jakarta font-extrabold text-navy text-base">
          How would you like to add this flight?
        </div>
        <button
          type="button"
          onClick={onCancel}
          aria-label="Cancel"
          className="w-9 h-9 rounded-full bg-bone/70 flex items-center justify-center"
        >
          <X size={18} weight="bold" color="#0E2C54" />
        </button>
      </div>
      {methods.map((m) => (
        <button
          key={m.id}
          type="button"
          data-testid={`flight-method-${m.id}`}
          onClick={() => onPick(m.id)}
          className="w-full bg-bone/40 hover:bg-bone/70 border border-navy/5 rounded-2xl p-3 flex items-center gap-3 text-left"
        >
          <div
            className="shrink-0 w-11 h-11 rounded-2xl flex items-center justify-center"
            style={{ backgroundColor: m.soft }}
          >
            <m.Icon size={22} weight="duotone" color={m.color} />
          </div>
          <div className="flex-1 min-w-0">
            <div className="font-jakarta font-extrabold text-navy text-base leading-tight">
              {m.title}
            </div>
            <div className="text-navy-soft text-sm font-jakarta font-medium mt-0.5">
              {m.sub}
            </div>
          </div>
          <CaretRight size={16} weight="bold" color="#4A5D78" />
        </button>
      ))}
    </div>
  );
}

/* ---------- The form ---------- */
/**
 * Flight add form. `mode === "scan"` auto-opens the camera on mount,
 * ships the photo to `/api/vault/scan-flight` for OCR, and pre-fills
 * the editable fields. `mode === "manual"` skips scanning and renders
 * the empty form.
 */
export default function FlightForm({ mode, onSave, onCancel }) {
  const { handle402 } = usePaywall();
  const [draft, setDraft] = useState({
    airline: "",
    flight_no: "",
    date: "",
    from: "",
    to: "",
    confirmation: "",
  });
  const [scanning, setScanning] = useState(false);
  const scanRef = useRef(null);

  // Auto-trigger camera on mount in scan mode.
  const triggeredRef = useRef(false);
  useEffect(() => {
    if (triggeredRef.current) return;
    triggeredRef.current = true;
    if (mode === "scan") setTimeout(() => scanRef.current?.click(), 200);
  }, [mode]);

  async function onScanFile(e) {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    if (!/^image\//.test(file.type)) {
      toast.error("Please upload an image");
      return;
    }
    const dataUrl = await resizeToDataUrl(file, 1600, 0.82);
    setScanning(true);
    try {
      const b64 = dataUrl.split(",", 2)[1];
      const r = await api.post(`/vault/scan-flight`, {
        image_base64: b64,
      });
      const f = r.data?.fields || {};
      setDraft((d) => ({
        ...d,
        airline: f.airline || d.airline,
        flight_no: f.flight_no || d.flight_no,
        date: f.date || d.date,
        from: f.from || d.from,
        to: f.to || d.to,
        confirmation: f.confirmation || d.confirmation,
      }));
      const filledCount = Object.values(f).filter(Boolean).length;
      if (filledCount === 0) {
        toast.message(
          "Couldn't read this boarding pass — try a brighter, sharper photo or fill manually"
        );
      } else {
        toast.success(
          `Auto-filled ${filledCount} fields — review before saving`
        );
      }
    } catch (err) {
      if (!handle402(err)) {
        toast.error(err?.response?.data?.detail || "Scan failed");
      }
    } finally {
      setScanning(false);
    }
  }

  function submit(e) {
    e?.preventDefault?.();
    const hasField = [
      "airline",
      "flight_no",
      "date",
      "from",
      "to",
      "confirmation",
    ].some((k) => draft[k]?.trim());
    if (!hasField) {
      toast.error("Fill at least one field");
      return;
    }
    onSave(draft);
  }

  return (
    <form
      onSubmit={submit}
      data-testid="flight-form"
      className="mt-5 bg-white rounded-2xl shadow-soft border border-navy/10 p-4 space-y-3"
    >
      <div className="flex items-center justify-between mb-1">
        <div className="font-jakarta font-extrabold text-navy text-base">
          {mode === "scan" ? "Scan boarding pass" : "New flight"}
        </div>
        <button
          type="button"
          onClick={onCancel}
          aria-label="Cancel"
          className="w-9 h-9 rounded-full bg-bone/70 flex items-center justify-center"
        >
          <X size={18} weight="bold" color="#0E2C54" />
        </button>
      </div>

      {/* Scan re-trigger button — only shown in scan mode. */}
      {mode === "scan" && (
        <button
          type="button"
          onClick={() => scanRef.current?.click()}
          data-testid="flight-rescan-btn"
          disabled={scanning}
          className="w-full h-20 rounded-xl border-2 border-dashed border-navy/20 bg-bone/40 flex items-center justify-center gap-2 text-navy-soft font-jakarta font-medium disabled:opacity-60"
        >
          {scanning ? (
            <>
              <CircleNotch
                size={20}
                weight="bold"
                className="animate-spin text-orange"
              />
              Reading your boarding pass…
            </>
          ) : (
            <>
              <Camera size={22} weight="duotone" color="#0E2C54" />
              <span className="text-sm">Tap to rescan boarding pass</span>
            </>
          )}
        </button>
      )}
      <input
        ref={scanRef}
        type="file"
        accept="image/*"
        capture="environment"
        className="hidden"
        onChange={onScanFile}
        data-testid="flight-scan-input"
      />

      <TextField
        label="Airline"
        placeholder="Delta"
        testId="vault-field-airline"
        value={draft.airline}
        onChange={(v) => setDraft((d) => ({ ...d, airline: v }))}
      />
      <TextField
        label="Flight #"
        placeholder="DL 263"
        testId="vault-field-flight_no"
        value={draft.flight_no}
        onChange={(v) => setDraft((d) => ({ ...d, flight_no: v }))}
      />
      <TextField
        label="Date"
        placeholder="Aug 14 2026"
        testId="vault-field-date"
        value={draft.date}
        onChange={(v) => setDraft((d) => ({ ...d, date: v }))}
      />
      <TextField
        label="From"
        placeholder="JFK"
        testId="vault-field-from"
        value={draft.from}
        onChange={(v) => setDraft((d) => ({ ...d, from: v }))}
      />
      <TextField
        label="To"
        placeholder="CDG"
        testId="vault-field-to"
        value={draft.to}
        onChange={(v) => setDraft((d) => ({ ...d, to: v }))}
      />
      <TextField
        label="Confirmation #"
        placeholder="ABCDE1"
        testId="vault-field-confirmation"
        value={draft.confirmation}
        onChange={(v) => setDraft((d) => ({ ...d, confirmation: v }))}
      />

      <button
        type="submit"
        data-testid="vault-save-btn"
        disabled={scanning}
        className="w-full bg-orange text-white font-jakarta font-extrabold text-base rounded-full py-3 shadow-soft disabled:opacity-60"
      >
        Save to Vault
      </button>
    </form>
  );
}
